import { html, render, nothing } from "lit-html";
import { unsafeHTML } from "lit-html/directives/unsafe-html";
import { ExtendedApiError } from "../../../components-shared/api/base/extended-api-error.js";
import { IExtendedApiResponse } from "../../../components-shared/api/base/i-extended-api-response.js";
import { ProductStatusTextType } from "../../../components-shared/enums/product-status-text-type.js";
import { CmsTooltip, TooltipPlacement }  from "../../website/cms-tooltip.js";

export class WarehouseStockList extends HTMLElement {
    productId: number;

    private _markup = (warehouse:IWarehouseDetailedStockInfo[]) => warehouse && warehouse.length ? 
        html `<ul class="warehouse-list" style="list-style:none; padding:0; margin:0;">
            ${warehouse.map(l => html`
            <li>
                <span class="warehouse-name">${l.warehouseName}:</span>
                <div class="warehouse-stock">
                    ${l.statusTextCss ? html`<span class="c-product-status ${l.statusTextCss}"></span>` : nothing }
                    <span class="warehouse-status-text">${l.statusText}</span>
                    ${l.detailedStockInfo ? html`
                        <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12">
                            <path id="ue91e-info" d="M6,0a6,6,0,1,0,6,6A6,6,0,0,0,6,0ZM7.249,9.3q-.463.183-.739.278a1.949,1.949,0,0,1-.641.1A1.277,1.277,0,0,1,5,9.4a.883.883,0,0,1-.31-.694,2.522,2.522,0,0,1,.023-.335c.016-.114.041-.242.075-.386l.387-1.365c.034-.131.063-.254.087-.371a1.646,1.646,0,0,0,.035-.322.5.5,0,0,0-.108-.364.609.609,0,0,0-.413-.1,1.084,1.084,0,0,0-.307.046c-.1.032-.195.061-.269.089l.1-.421q.379-.152.726-.265A2.146,2.146,0,0,1,5.678,4.8a1.254,1.254,0,0,1,.859.269.894.894,0,0,1,.3.7q0,.089-.021.313a2.1,2.1,0,0,1-.077.412L6.357,7.852c-.031.109-.059.234-.085.374a1.977,1.977,0,0,0-.037.318.457.457,0,0,0,.121.37.68.68,0,0,0,.42.1,1.219,1.219,0,0,0,.318-.051,1.808,1.808,0,0,0,.257-.086ZM7.181,3.773a.918.918,0,0,1-.648.25.927.927,0,0,1-.65-.25.817.817,0,0,1,0-1.215.92.92,0,0,1,.65-.254.91.91,0,0,1,.648.254.818.818,0,0,1,0,1.213Z" fill="#222226"/>
                        </svg>
                        ${this._buildTooltip(l.detailedStockInfo)}
                    ` : nothing }
                </div>
            </li>`)}
        </ul>` : nothing;

    private _buildTooltip(stockInfo: IDetailedStockInfo) {
        const tooltipBody = document.createElement("div");
        const tooltipLitMarkup = html`
            ${stockInfo.inStockText ? html`<span class="in-stock-text">${stockInfo.inStockText}</span>` : nothing }
            ${stockInfo.productStockInfo?.length > 0 ?
                html `<hr/><ul style="list-style:none; padding:0; margin:0;">
                    ${stockInfo.productStockInfo.map(stock => html`
                        <li style="list-style:none; padding:0; margin:0;">
                            ${stock.statusText ? html`<span class="stock-status-text">${unsafeHTML(stock.statusText)}</span>` : nothing }
                            ${stock.expectedInStockText ? html`<span class="expected-in-stock">${stock.expectedInStockText}</span>` : nothing}
                        </li>
                    `) }
                </ul>`
            : nothing}
        `;

        render(tooltipLitMarkup, tooltipBody);

        const discountDetailsTooltip = new CmsTooltip({
            placement: TooltipPlacement.Bottom,
            context: this,
            bodyTemplate: tooltipBody,
        });

        return discountDetailsTooltip;
    }

    hasData: boolean;

    constructor(productId: number) {
        super();
        this.productId = productId;
    }

    getProductIdAttr(): number {
        const productIdAttr = this.getAttribute("product-id");
        if (productIdAttr && productIdAttr.length) {
            return parseInt(productIdAttr);
        }

        return null;
    }

    async update(productId: number) {
        this.productId = productId;
        await this.load();
    }

    public get hasWarehouseStockInfo() {
        return this.hasData;
    }

    async attributeChangedCallback() {
        const newProductId = this.getProductIdAttr();

        if (newProductId) {
            await this.update(newProductId);
        }
    }

    async connectedCallback() {
        if (!this.productId) {
            this.productId = this.getProductIdAttr();
        }

        await this.load();
    }

    async load(): Promise<void> {
        if (this.productId) {
            const response = await fetch(`/api/estore/stock/${this.productId}/detailed-stock-info`);

            const dataResponse = await response.json() as IExtendedApiResponse<IWarehouseDetailedStockInfo[]>;

            if (!dataResponse || dataResponse.hasError) {
                throw new ExtendedApiError(dataResponse);
            }

            this.hasData = dataResponse.result?.length > 0;

            this.render(dataResponse.result);
        }
    }

    render(warehouseLines: IWarehouseDetailedStockInfo[]) {
        //We don't load data immediately on connectedCallback at product view, so we need to check if data is available.
        //But if product-id attribute is set then we have to load data.
        if(warehouseLines){
            render(this._markup(warehouseLines), this);
        }
    }
}

customElements.define("warehouse-stock-list", WarehouseStockList);


interface IWarehouseDetailedStockInfo {
    warehouseName: string,
    statusText: string,
    statusTextCss?: string,
    detailedStockInfo?: IDetailedStockInfo,
}

interface IDetailedStockInfo {
    inStockText?: string,
    productStockInfo?: IProductStockInfo[],
}

interface IProductStockInfo {
    productStatusType: ProductStatusTextType,
    orderedQuantity?: number,
    expectedInStockText?: string,
    statusText?: string,
}